// src/components/ChatInterface.js
"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate,useLocation } from "react-router-dom";
import {
  AppBar, Toolbar, Container, TextField, Button, Typography, Paper,
  Box, IconButton, Tooltip, Stack, Avatar, Dialog, DialogTitle,
  DialogContent, DialogActions, InputAdornment, LinearProgress, Chip, Fab,
  List, ListItemButton, ListItemText, Divider, CircularProgress,
  Backdrop, Popover, Alert
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ReplayIcon from "@mui/icons-material/Replay";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DescriptionIcon from "@mui/icons-material/Description";
import LinkIcon from "@mui/icons-material/Link";
import axios from "axios";
import { toast } from "react-toastify";
import ThemeToggleButton from "./ThemeToggleButton";

const API_BASE = process.env.REACT_APP_API;

/* ---------- Coachmark (tour popover) ---------- */
function Coachmark({
  open, anchorEl, title, body, step, total, onNext, onPrev, onSkip, onDone
}) {
  const [fallbackPos] = React.useState({
    top: typeof window !== "undefined" ? Math.round(window.innerHeight * 0.25) : 200,
    left: typeof window !== "undefined" ? Math.round(window.innerWidth / 2) : 300,
  });

  return (
    <>
      <Backdrop
        open={open}
        sx={{ zIndex: (t) => t.zIndex.modal - 2, bgcolor: "rgba(0,0,0,0.35)" }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl || undefined}
        anchorReference={anchorEl ? "anchorEl" : "anchorPosition"}
        anchorPosition={fallbackPos}
        onClose={onSkip}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{ sx: { p: 2, borderRadius: 2, maxWidth: 360, boxShadow: 6 } }}
      >
        <Stack spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{title}</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>{body}</Typography>
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ pt: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Step {step + 1} of {total}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={onSkip}>Skip</Button>
              {step > 0 && <Button size="small" onClick={onPrev}>Back</Button>}
              {step < total - 1 ? (
                <Button size="small" variant="contained" onClick={onNext}>Next</Button>
              ) : (
                <Button size="small" variant="contained" onClick={onDone}>Done</Button>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}

/* ---------- Onboarding message (posted once per assistant) ---------- */
const onboardingMessage = () => {
  return (
`# Welcome! How to use & train your assistant

**Train me with your content:**
1. Open your assistant → **Training** tab.
2. **Upload files** (PDF, DOCX, PPTX, CSV/XLSX, TXT) or **paste a URL** (YouTube supported).
3. Choose **Auto** or **Manual** training:
   - Auto → I index right away.
   - Manual → files queue; click **Train Now** to process in one go.
4. When training finishes, the status shows **Trained**.

**Chat effectively:**
- Ask in plain English. Be specific for best results.
- I’ll show sources under my answers. Click to open the file or link.

**Keep me updated:**
- Re-upload changed files. In Auto mode I re-train instantly; in Manual mode click **Train Now**.

**Troubleshooting:**
- If I don’t cite a file, confirm it says **Used in Training**.
- Big PDFs can take longer; check the status chip.
- Still stuck? Try re-uploading or switch to Manual and press **Train Now**.

Tip: Start with “Summarize the key points from my training files” or “Where does the policy mention reimbursements?”`
  );
};

const ChatInterface = ({
  assistantId = "",
  userId=null,
  initial = "",
  hideHeader = false,
  onStatusChange = () => {} , isActiveTab = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const rootRef = useRef(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Fullscreen panel heights (allow some space for sticky headers/toolbars)
  const headerSubtract = hideHeader ? 112 : 176;
  const panelHeight = `calc(100vh - ${headerSubtract}px)`;

  // Subtle themed background for the message scroller
  const messagesBg = useMemo(() => {
    const top = alpha(theme.palette.background.default, isDark ? 0.6 : 0.9);
    const base = theme.palette.background.paper;
    return `linear-gradient(180deg, ${top} 0%, ${base} 120px)`;
  }, [theme, isDark]);

  // ----- global-ish -----

  const USER_ID = useMemo(() => {
    const fromProp = (userId ?? "").toString().trim();
    if (fromProp) return fromProp;
    try {
      return (
        localStorage.getItem("userId") ||
        sessionStorage.getItem("userId") ||
        null
      );
    } catch {
      return null;
    }
  }, [userId]);

  const USER_INITIAL = useMemo(
    () => (initial ? (initial.trim()[0] || "U").toUpperCase() : "U"),
    [initial]
  );

  /* ------------------ Source opener ------------------ */
  const openReference = async (ref) => {
    const name = typeof ref === "string" ? ref : (ref.name || "");
    const key  = typeof ref === "string" ? ref : (ref.key  || "");

    if (/^https?:\/\//i.test(name)) {
      window.open(name, "_blank", "noopener,noreferrer");
      return;
    }
    if (String(key).includes("/urls/") && /^https?:\/\//i.test(key)) {
      window.open(key, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const res = await axios.get(`${API_BASE}/assistants/${assistantId}/file-url`, { params: { key } });
      const url = res?.data?.url;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else toast.info("Preview not available yet.");
    } catch {
      toast.error("Couldn't open the source file");
    }
  };

  const [assistant, setAssistant] = useState(null); // {Name, Description, Trained, ...}

  // ----- threads & messages -----
  const [threads, setThreads] = useState([]);
  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);

  // OpenAI thread id per Dynamo thread
  const [oaiThreadMap, setOaiThreadMap] = useState({}); // { [ThreadID]: openaiThreadId }

  // ----- composer / UI -----
  const [chatInput, setChatInput] = useState("");
  const [search, setSearch] = useState("");
  const [threadSearch, setThreadSearch] = useState("");
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [isTyping, setIsTyping] = useState(false);

  // Upgrade banner on entitlement breach (402)
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeMsg, setUpgradeMsg] = useState("You've hit your plan limit. Upgrade to continue.");

  // New chat dialog
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [newChatName, setNewChatName] = useState("");

  // Rename dialog
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  
  // first chat ref
  const onboardingGuardRef = useRef(false);

  // scroll mgmt
  const scrollerRef = useRef(null);
  const [showScrollFab, setShowScrollFab] = useState(false);
  const lastUserMsgRef = useRef("");

  // ---------- TOUR anchors ----------
  const newChatBtnRef = useRef(null);
  const threadSearchRef = useRef(null);
  const listRef = useRef(null);
  const regenerateBtnRef = useRef(null);
  const composerInputRef = useRef(null);
  const sendBtnRef = useRef(null);

  // Tour state
  const [tour, setTour] = useState({ open: false, step: 0 });
  const tourSteps = [
    { title: "Start a New Chat", body: "Create a fresh conversation with this assistant.", ref: newChatBtnRef },
    { title: "Find a Conversation", body: "Search across your conversation titles and messages.", ref: threadSearchRef },
    { title: "Conversation List", body: "All your threads live here. Click any thread to open it.", ref: listRef },
    { title: "Messages", body: "Your chat history appears here. Scroll to review or copy.", ref: scrollerRef },
    { title: "Regenerate Answer", body: "Try a different answer to your latest question.", ref: regenerateBtnRef },
    { title: "Compose", body: "Type your question. Enter sends; Shift+Enter adds a new line.", ref: composerInputRef },
    { title: "Send", body: "Fire it off! The assistant will reply here in the thread.", ref: sendBtnRef },
  ];

  const startTour = () => setTour({ open: true, step: 0 });
  const endTour   = () => { setTour({ open: false, step: 0 });};
  const nextStep  = () => setTour((t) => ({ ...t, step: Math.min(t.step + 1, tourSteps.length - 1) }));
  const prevStep  = () => setTour((t) => ({ ...t, step: Math.max(t.step - 1, 0) }));

  useEffect(() => {
    if (USER_ID) {
      try { localStorage.setItem("userId", USER_ID); } catch {}
    }
  }, [USER_ID]);

  /* ------------------ TOUR auto-start (once per mount) ------------------ */
  const tourShownThisMountRef = useRef(false);
  useEffect(() => {
    if (isActiveTab && !tourShownThisMountRef.current) {
      tourShownThisMountRef.current = true;
      const t = setTimeout(() => setTour({ open: true, step: 0 }), 350);
      return () => clearTimeout(t);
    }
    if (!isActiveTab) {
      setTour({ open: false, step: 0 });
    }
  }, [isActiveTab]);

  useEffect(() => {
    if (!tour.open) return;
    const el = tourSteps[tour.step]?.ref?.current || null;
    if (el && "scrollIntoView" in el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
    }
  }, [tour.open, tour.step]);

  useEffect(() => {
    if (!tour.open) return;
    const onKey = (e) => { if (e.key === "Escape") endTour(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tour.open]);

  // ----------------------------------
  // helpers
  // ----------------------------------
  const is402 = (err) => !!(err && err.response && err.response.status === 402);

  const triggerUpgrade = (msg) => {
    setUpgradeMsg(msg || "You've hit your plan limit. Upgrade to continue.");
    setShowUpgrade(true);
  };

  const dedupeAndSortThreads = (items = []) => {
    const map = new Map();
    for (const t of items) {
      const prev = map.get(t.ThreadID);
      if (!prev) {
        map.set(t.ThreadID, t);
      } else {
        const prevTs = prev.LastMessageAt ? new Date(prev.LastMessageAt).getTime() : 0;
        const curTs = t.LastMessageAt ? new Date(t.LastMessageAt).getTime() : 0;
        map.set(t.ThreadID, curTs >= prevTs ? t : prev);
      }
    }
    const arr = [...map.values()];
    arr.sort((a, b) => {
      const at = a.LastMessageAt ? new Date(a.LastMessageAt).getTime() : 0;
      const bt = b.LastMessageAt ? new Date(b.LastMessageAt).getTime() : 0;
      return bt - at;
    });
    return arr;
  };

  const deriveTitleFromText = (txt) => {
    const first = String(txt).split("\n").find((l) => l.trim()) || String(txt);
    const cleaned = first.replace(/[`*_#>]+/g, "").trim();
    if (!cleaned) return "New chat";
    return cleaned.length > 60 ? cleaned.slice(0, 57) + "…" : cleaned;
  };

  const fetchAssistant = async () => {
    if (!assistantId) { setAssistant(null); return; }
    try {
      const res = await axios.get(`${API_BASE}/assistants/${assistantId}`);
      setAssistant(res.data);
    } catch {
      setAssistant(null);
    }
  };

  const selectThread = (threadId, messages = null) => {
    setSelectedThreadId(threadId);
    const thread = threads.find((t) => t.ThreadID === threadId);
    const msgs = messages ?? (thread?.Messages || []);
    setChatMessages(
      msgs.map((m) => ({
        role: m.Role,
        content: m.Content,
        ts: m.Ts,
        // allow backend to optionally persist sources; else empty
        sources: m.Sources || m.sources || [],
      }))
    );
    requestAnimationFrame(() => {
      const el = scrollerRef.current;
      if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    });
  };

  const fetchThreads = async () => {
    if (!assistantId) return;
    try {
      setIsLoadingThreads(true);
      const res = await axios.get(`${API_BASE}/assistants/${assistantId}/chats`, {
        params: { user_id: USER_ID },
      });
      const items = dedupeAndSortThreads(res.data || []);
      setThreads(items);

      // If nothing selected yet, pick the first immediately
      if (!selectedThreadId && items.length) {
        const first = items[0];
        setSelectedThreadId(first.ThreadID);
        const msgs = (first.Messages || []).map((m) => ({
          role: m.Role, content: m.Content, ts: m.Ts, sources: m.Sources || m.sources || [],
        }));
        setChatMessages(msgs);
      } else if (selectedThreadId) {
        const current = items.find((t) => t.ThreadID === selectedThreadId);
        if (current) {
          const msgs = (current.Messages || []).map((m) => ({
            role: m.Role, content: m.Content, ts: m.Ts, sources: m.Sources || m.sources || [],
          }));
          setChatMessages(msgs);
        } else {
          if (items.length) {
            const first = items[0];
            setSelectedThreadId(first.ThreadID);
            const msgs = (first.Messages || []).map((m) => ({
              role: m.Role, content: m.Content, ts: m.Ts, sources: m.Sources || m.sources || [],
            }));
            setChatMessages(msgs);
          } else {
            setSelectedThreadId(null);
            setChatMessages([]);
          }
        }
      }
    } catch (e) {
      if (is402(e)) triggerUpgrade(e.response?.data?.error || upgradeMsg);
      else toast.error("Failed to load chat history");
    } finally {
      setIsLoadingThreads(false);
    }
  };

  const createNewThread = async (title = "New chat") => {
    if (!assistantId) {
      toast.warning("Please train or select an assistant first.");
      return null;
    }
    try {
      const res = await axios.post(`${API_BASE}/assistants/${assistantId}/chats`, {
        user_id: USER_ID,
        title,
      });
      const thread = res.data;
      setThreads((prev) => dedupeAndSortThreads([thread, ...prev]));
      setSelectedThreadId(thread.ThreadID);
      setChatMessages([]);
      const oai = await axios.get(`${API_BASE}/start`);
      setOaiThreadMap((m) => ({ ...m, [thread.ThreadID]: oai.data.thread_id }));
      return thread.ThreadID;
    } catch (e) {
      if (is402(e)) triggerUpgrade(e.response?.data?.error || upgradeMsg);
      else toast.error("Failed to create a new chat thread");
      return null;
    }
  };

  const ensureOpenAIThreadFor = async (threadId) => {
    if (oaiThreadMap[threadId]) return oaiThreadMap[threadId];
    const oai = await axios.get(`${API_BASE}/start`);
    const tid = oai.data.thread_id;
    setOaiThreadMap((m) => ({ ...m, [threadId]: tid }));
    return tid;
  };

  const appendToDynamo = async (threadId, role, content, ts = Date.now(), meta = {}) => {
    try {
      await axios.post(`${API_BASE}/assistants/${assistantId}/chats/${threadId}/append`, { role, content, ts });
      setChatMessages((prev) => [
        ...prev,
        {
          role,
          content,
          ts,
          // attach sources to assistant messages so they render under the bubble
          sources: role === "assistant" && Array.isArray(meta?.sources) ? meta.sources : [],
        },
      ]);
      setThreads((prev) => {
        const next = [...prev];
        const idx = next.findIndex((t) => t.ThreadID === threadId);
        if (idx >= 0) {
          const updated = {
            ...next[idx],
            LastMessageAt: new Date(ts).toISOString(),
            Messages: [...(next[idx].Messages || []), { Role: role, Content: content, Ts: ts }],
          };
          next.splice(idx, 1);
          next.unshift(updated);
        }
        return next;
      });

      if (role === "assistant") {
        const faqs = Array.isArray(meta?.related_questions) ? meta.related_questions : [];
        if (faqs.length) setSidebarFaqs(faqs);
      }
    } catch (e) {
      if (is402(e)) triggerUpgrade(e.response?.data?.error || upgradeMsg);
      else console.warn("Dynamo append failed");
    }
  };

  const tryAutoTitle = async (threadId, text) => {
    const thread = threads.find((t) => t.ThreadID === threadId);
    if (!thread) return;
    const current = thread.Title || "";
    if (current && current !== "New chat") return;
    const derived = deriveTitleFromText(text);
    try {
      await axios.put(`${API_BASE}/assistants/${assistantId}/chats/${threadId}`, { title: derived });
      setThreads((prev) => prev.map((t) => (t.ThreadID === threadId ? { ...t, Title: derived } : t)));
    } catch {}
  };

  const renameThread = async (newTitle) => {
    if (!selectedThreadId || !assistantId) return;
    try {
      await axios.put(`${API_BASE}/assistants/${assistantId}/chats/${selectedThreadId}`, {
        title: newTitle.trim() || "New chat",
      });
      setThreads((prev) =>
        prev.map((t) =>
          t.ThreadID === selectedThreadId ? { ...t, Title: newTitle.trim() || "New chat" } : t
        )
      );
      toast.success("Chat renamed");
    } catch (e) {
      if (is402(e)) triggerUpgrade(e.response?.data?.error || upgradeMsg);
      else toast.error("Rename failed");
    }
  };

  const deleteThread = async () => {
    if (!selectedThreadId || !assistantId) return;
    try {
      await axios.delete(`${API_BASE}/assistants/${assistantId}/chats/${selectedThreadId}`);
      setDeleteOpen(false);
      setThreads((prev) => prev.filter((t) => t.ThreadID !== selectedThreadId));
      setSelectedThreadId(null);
      const remaining = threads.filter((t) => t.ThreadID !== selectedThreadId);
      if (remaining.length) {
        const next = dedupeAndSortThreads(remaining)[0];
        setSelectedThreadId(next.ThreadID);
        const msgs = (next.Messages || []).map((m) => ({
          role: m.Role, content: m.Content, ts: m.Ts, sources: m.Sources || m.sources || [],
        }));
        setChatMessages(msgs);
      } else {
        setChatMessages([]);
      }
      toast.success("Chat deleted");
    } catch (e) {
      if (is402(e)) triggerUpgrade(e.response?.data?.error || upgradeMsg);
      else toast.error("Delete failed");
    }
  };

  // ----------------------------------
  // effects
  // ----------------------------------
  useEffect(() => {
    setThreads([]);
    setSelectedThreadId(null);
    setChatMessages([]);
  }, [assistantId]);

  useEffect(() => { fetchAssistant(); }, [assistantId]);
  useEffect(() => { fetchThreads(); }, [assistantId]);

  // Auto-select first thread whenever threads arrive/refresh
  useEffect(() => {
    if (!selectedThreadId && threads.length) {
      selectThread(threads[0].ThreadID);
    }
  }, [threads, selectedThreadId]); // ensures first is active without manual click

  // Post one-time onboarding message into a new chat for this assistant
  const [sidebarFaqs, setSidebarFaqs] = useState([]); // array of strings
  useEffect(() => {
    const key = assistantId ? `onboarding_shown_${assistantId}` : null;
    if (!assistantId || !key) return;
    if (isLoadingThreads) return;

    if (onboardingGuardRef.current) return;

    const gate = (() => {
      try { return localStorage.getItem(key); } catch { return null; }
    })();

    if (gate === "1" || gate === "pending") return;

    if (threads.length === 0) {
      onboardingGuardRef.current = true;

      try { localStorage.setItem(key, "pending"); } catch {}

      (async () => {
        try {
          const tid = await createNewThread("Getting started");
          if (tid) {
            await appendToDynamo(tid, "assistant", onboardingMessage(), Date.now(), {});
            selectThread(tid);
            try { localStorage.setItem(key, "1"); } catch {}
          } else {
            try { localStorage.removeItem(key); } catch {}
          }
        } finally {
          onboardingGuardRef.current = false;
        }
      })();
    }
  }, [assistantId, threads.length, isLoadingThreads]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      setShowScrollFab(!nearBottom);
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [chatMessages, isTyping]);

  // Bubble typing status up to Workspace
  useEffect(() => {
    onStatusChange({ isTyping });
  }, [isTyping, onStatusChange]);

  // ----------------------------------
  // actions
  // ----------------------------------
  const openNewChatDialog = () => {
    if (!assistantId) return toast.warning("Select/train an assistant first.");
    setNewChatName("");
    setNewChatOpen(true);
  };

  const createChatFromDialog = async () => {
    const title = newChatName.trim() || "New chat";
    setNewChatOpen(false);
    const tid = await createNewThread(title);
    if (tid) toast.success("New chat created");
  };

  const handleSendMessage = async (overrideText) => {
    if (overrideText == undefined || overrideText.type === "click") {
      overrideText = null;
    }

    const toSend = (overrideText ?? chatInput).trim();
    if (!toSend) return toast.error("Please enter a message");
    if (!assistantId) return toast.warning("No assistant selected/trained.");

    let threadId = selectedThreadId;
    if (!threadId) {
      const autoTitle = deriveTitleFromText(toSend);
      threadId = await createNewThread(autoTitle || "New chat");
      if (!threadId) return;
    }

    const userMsg = { role: "user", content: toSend, ts: Date.now() };
    lastUserMsgRef.current = userMsg.content;

    if (overrideText == null) setChatInput("");
    setIsTyping(true);

    await appendToDynamo(threadId, "user", userMsg.content, userMsg.ts);
    await tryAutoTitle(threadId, userMsg.content);

    try {
      const openaiThreadId = await ensureOpenAIThreadFor(threadId);
      const payload = { thread_id: openaiThreadId, message: userMsg.content, assistant_id: assistantId };
      const response = await axios.post(`${API_BASE}/chat`, payload);

      const assistantText = response?.data?.response || "(no response)";
      const meta = {
        sources: response?.data?.sources || [],
        related_questions: response?.data?.related_questions || []
      };

      await appendToDynamo(threadId, "assistant", assistantText, Date.now(), meta);
      if (meta.related_questions?.length) setSidebarFaqs(meta.related_questions);
      else if (!sidebarFaqs.length) setSidebarFaqs(defaultFaqs(userMsg.content));
    } catch (e) {
      if (is402(e)) triggerUpgrade(e.response?.data?.error || upgradeMsg);
      else toast.error("Failed to send message");
    } finally {
      setIsTyping(false);
    }
  };

  const regenerateLast = async () => {
    const lastUser = lastUserMsgRef.current;
    if (!lastUser) return toast.info("No previous user message to regenerate");
    if (!assistantId || !selectedThreadId) return;

    setIsTyping(true);
    try {
      const openaiThreadId = await ensureOpenAIThreadFor(selectedThreadId);
      const payload = { thread_id: openaiThreadId, message: lastUser, assistant_id: assistantId };
      const response = await axios.post(`${API_BASE}/chat`, payload);
      const assistantText = response?.data?.response || "(no response)";
      const meta = {
        sources: response?.data?.sources || [],
        related_questions: response?.data?.related_questions || []
      };
      await appendToDynamo(selectedThreadId, "assistant", assistantText, Date.now(), meta);
      if (meta.related_questions?.length) setSidebarFaqs(meta.related_questions);
    } catch (e) {
      if (is402(e)) triggerUpgrade(e.response?.data?.error || upgradeMsg);
      else toast.error("Failed to regenerate");
    } finally {
      setIsTyping(false);
    }
  };

  const copyMessage = async (idx) => {
    try {
      await navigator.clipboard.writeText(chatMessages[idx].content);
      setCopiedIndex(idx);
      setTimeout(() => setCopiedIndex(null), 1200);
    } catch {
      toast.error("Copy failed");
    }
  };

  const askFaq = async (q) => {
    await handleSendMessage(q);
  };

  // ----------------------------------
  // derived
  // ----------------------------------
  const filteredMessages = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return chatMessages;
    return chatMessages.filter((m) => (m.content || "").toLowerCase().includes(q));
  }, [chatMessages, search]);

  const filteredThreads = useMemo(() => {
    const q = threadSearch.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((t) =>
      (t.Title || "New chat").toLowerCase().includes(q) ||
      (t.Messages || []).some((m) => (m.Content || "").toLowerCase().includes(q))
    );
  }, [threads, threadSearch]);

  const activeThread = threads.find((t) => t.ThreadID === selectedThreadId);

  const scrollToBottom = () => {
    const el = scrollerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ----------------------------------
  // UI
  // ----------------------------------
  return (
    <Box ref={rootRef} sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Optional local header (Workspace can hide this) */}
      {!hideHeader && (
        <AppBar position="sticky" color="inherit" sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
          <Toolbar sx={{ gap: 1.5, minHeight: 64 }}>
            <Tooltip title="Back to Assistants">
              <IconButton edge="start" onClick={() => navigate("/")}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>

            <Box sx={{ display: "flex", flexDirection: "column", ml: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
                Chat
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            <ThemeToggleButton />

            <Tooltip title="Take a quick tour">
              <Button
                size="small"
                variant="outlined"
                startIcon={<HelpOutlineIcon />}
                onClick={startTour}
                sx={{ ml: 1 }}
              >
                Take a tour
              </Button>
            </Tooltip>

            {assistant?.Trained ? (
              <Chip color="success" size="small" label="Assistant trained" sx={{ ml: 1 }} />
            ) : (
              <Chip size="small" color="warning" variant="outlined" label="Not trained" sx={{ ml: 1 }} />
            )}
          </Toolbar>
          {isTyping && <LinearProgress color="primary" />}
        </AppBar>
      )}

      {/* Full-bleed container */}
      <Container maxWidth={false} sx={{ py: 2, px: { xs: 1.5, md: 2.5 } }}>
        {/* Upgrade Banner (shows on any 402) */}
        {showUpgrade && (
          <Alert
            severity="warning"
            variant="outlined"
            sx={{
              mb: 1.5,
              borderRadius: 2,
              borderColor: "warning.light",
              bgcolor: (t) => alpha(t.palette.warning.main, 0.06),
              display: "flex",
              alignItems: "center",
            }}
            action={
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate("/billing")}
                >
                  Upgrade plan
                </Button>
                <IconButton
                  size="small"
                  onClick={() => setShowUpgrade(false)}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </Stack>
            }
          >
            {upgradeMsg}
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { md: "320px 1fr 320px" },
            gap: 2,
            alignItems: "stretch",
          }}
        >
          {/* -------- Left: Conversations panel -------- */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              height: { xs: "auto", md: panelHeight },
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Conversations</Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Tooltip title="New chat">
                <span>
                  <IconButton
                    color="primary"
                    onClick={openNewChatDialog}
                    disabled={!assistantId}
                    ref={newChatBtnRef}
                  >
                    <AddIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Refresh">
                <span>
                  <IconButton onClick={fetchThreads} disabled={isLoadingThreads}>
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Take a quick tour">
                <span>
                  <IconButton onClick={startTour}>
                    <HelpOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {/* thread search */}
            <TextField
              size="small"
              placeholder="Search conversations…"
              value={threadSearch}
              onChange={(e) => setThreadSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                inputRef: (el) => { threadSearchRef.current = el; }
              }}
            />

            <Divider sx={{ my: 1.25 }} />

            <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5 }} ref={listRef}>
              {isLoadingThreads ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : filteredThreads.length ? (
                <List dense sx={{ py: 0 }}>
                  {filteredThreads.map((t) => {
                    const isSelected = t.ThreadID === selectedThreadId;
                    const lastMsg = (t.Messages || []).slice().pop();
                    const lastSnippet =
                      (lastMsg?.Content || "").replace(/\s+/g, " ").slice(0, 80) +
                      ((lastMsg?.Content || "").length > 80 ? "…" : "");
                    const initials = (t.Title || "N").trim()[0]?.toUpperCase() || "N";

                    return (
                      <ListItemButton
                        key={t.ThreadID}
                        selected={isSelected}
                        onClick={() => selectThread(t.ThreadID)}
                        sx={{
                          borderRadius: 2,
                          mb: 0.75,
                          px: 1,
                          "&.Mui-selected": {
                            bgcolor: alpha(theme.palette.primary.main, isDark ? 0.22 : 0.08),
                            border: "1px solid",
                            borderColor: "primary.light",
                          },
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 34, height: 34, mr: 1.25,
                            bgcolor: isSelected ? "primary.main" : "secondary.main",
                            fontSize: 14, fontWeight: 700,
                          }}
                        >
                          {initials}
                        </Avatar>
                        <ListItemText
                          primary={
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                {t.Title || "Untitled chat"}
                              </Typography>
                              {isSelected && <Chip size="small" label="Active" color="primary" variant="outlined" />}
                            </Stack>
                          }
                          secondary={
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary", display: "block" }}
                              noWrap
                            >
                              {lastSnippet || "No messages yet"}
                            </Typography>
                          }
                          secondaryTypographyProps={{ component: "div" }}
                        />
                        <Typography variant="caption" sx={{ color: "text.secondary", ml: 1 }}>
                          {t.LastMessageAt ? new Date(t.LastMessageAt).toLocaleDateString() : ""}
                        </Typography>
                      </ListItemButton>
                    );
                  })}
                </List>
              ) : (
                <Box sx={{ color: "text.secondary", fontSize: 14, p: 2 }}>
                  No conversations yet. Click the + to start one.
                </Box>
              )}
            </Box>
          </Paper>

          {/* -------- Center: Conversation pane (FULL SCREEN HEIGHT) -------- */}
          <Paper
            elevation={0}
            sx={{
            p: 2,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            minHeight: 440,
            height: panelHeight,
            display: "flex",
            flexDirection: "column",
            position: "relative",           // ⬅️ make this the anchor
          }}
          >
            {/* thread header */}
            <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                {activeThread?.Title || "No chat selected"}
              </Typography>

              {selectedThreadId && (
                <>
                  <Tooltip title="Rename chat">
                    <IconButton size="small" onClick={() => { setRenameValue(activeThread?.Title || ""); setRenameOpen(true); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete chat">
                    <IconButton size="small" color="error" onClick={() => setDeleteOpen(true)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}

              <Box sx={{ flexGrow: 1 }} />
            </Box>

            {/* Search in this chat + Regenerate button */}
            <TextField
              size="small"
              placeholder="Search in this chat…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ mb: 1 }}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Regenerate last answer">
                      <span>
                        <IconButton
                          onClick={regenerateLast}
                          disabled={!lastUserMsgRef.current || isTyping}
                          ref={regenerateBtnRef}
                        >
                          <ReplayIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            {/* messages */}
            <Box
              ref={scrollerRef}
              sx={{
                flex: 1,
                overflowY: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 1.25,
                pb: showScrollFab ? 7 : 1.25,
                background: messagesBg,
              }}
            >
              {filteredMessages.length === 0 && !isTyping ? (
                <EmptyState />
              ) : (
                <Stack spacing={1.25}>
                  {filteredMessages.map((msg, index) => (
                    <MessageBubble
                      key={`${msg.ts || index}-${index}`}
                      role={msg.role}
                      content={msg.content}
                      timestamp={msg.ts}
                      onCopy={() => copyMessage(index)}
                      copied={copiedIndex === index}
                      userInitial={USER_INITIAL}
                      sources={msg.sources || []}
                      onOpenSource={openReference}
                    />
                  ))}
                  {isTyping && (
                    <Box display="flex" justifyContent="flex-start" mb={1}>
                      <TypingDots />
                    </Box>
                  )}
                </Stack>
              )}
            </Box>

            {/* composer */}
            <Box sx={{ mt: 1.25 }}>
              <TextField
                fullWidth
                placeholder={assistantId ? "Type your message…" : "Train/select an assistant to chat…"}
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={onKeyDown}
                disabled={!assistantId}
                multiline
                minRows={2}
                maxRows={8}
                inputRef={composerInputRef}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ gap: 1 }}>
                      <Tooltip title="New chat">
                        <span>
                          <IconButton onClick={openNewChatDialog} sx={{ mr: 0.5 }} disabled={!assistantId}>
                            <AddIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Button
                        variant="contained"
                        endIcon={<SendIcon />}
                        onClick={handleSendMessage}
                        disabled={isTyping || !chatInput.trim() || !assistantId}
                        ref={sendBtnRef}
                      >
                        Send
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Paper>

          {/* -------- Right: FAQs only -------- */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              height: { xs: "auto", md: panelHeight },
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              overflow: "hidden",               // ⬅️ clip contents to panel
            }}
          >
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Frequently asked
              </Typography>
            </Box>

            {/* scrollable area for FAQs */}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
              <Stack spacing={1}>
                {(sidebarFaqs.length ? sidebarFaqs : defaultFaqs()).map((q, idx) => (
                  <Button
                    key={idx}
                    variant="outlined"
                    size="small"
                    onClick={() => askFaq(q)}
                    fullWidth
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: 2,
                      whiteSpace: "normal",      // ⬅️ allow wrapping
                      textAlign: "left",
                      alignItems: "flex-start",
                      lineHeight: 1.25,
                      wordBreak: "break-word",   // ⬅️ prevent spill on long words/URLs
                      px: 1.25,
                      py: 1,
                    }}
                  >
                    {q}
                  </Button>
                ))}
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Container>

      {/* Scroll to bottom */}
      {showScrollFab && (
        <Fab
          color="primary"
          size="small"
          onClick={scrollToBottom}
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: 96,                     // sits above the composer
            zIndex: (t) => t.zIndex.fab,
            boxShadow: 6,
          }}
        >
          <ArrowDownwardIcon />
        </Fab>
      )}


      {/* New Chat dialog */}
      <Dialog open={newChatOpen} onClose={() => setNewChatOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create new chat</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            label="Chat name (optional)"
            value={newChatName}
            onChange={(e) => setNewChatName(e.target.value)}
            placeholder="e.g., Pricing Q&A"
            margin="dense"
          />
          <Typography variant="caption" color="text.secondary">
            If left blank, the first message will auto-name this conversation.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewChatOpen(false)}>Cancel</Button>
          <Button onClick={createChatFromDialog} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Rename chat</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            label="Chat name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            margin="dense"
            placeholder="Give this conversation a clear title"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              await renameThread(renameValue);
              setRenameOpen(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Delete this chat?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. The conversation and its messages will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={deleteThread}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* COACHMARK INSTANCE */}
      <Coachmark
        open={tour.open}
        anchorEl={tourSteps[tour.step]?.ref?.current || null}
        title={tourSteps[tour.step]?.title || ""}
        body={tourSteps[tour.step]?.body || ""}
        step={tour.step}
        total={tourSteps.length}
        onNext={nextStep}
        onPrev={prevStep}
        onSkip={endTour}
        onDone={endTour}
      />
    </Box>
  );
};

/* ---------- Helpers / Subcomponents ---------- */

const defaultFaqs = (seed = "") => {
  // Existing + additional doc-centric prompts users commonly ask
  return [
    "How do I upload a PDF or link to train the assistant?",
    "What’s the difference between Auto vs Manual training?",
    "How do I retrain after I replace a file?",
    "How can I view citations and open the source files?",
    "Why isn’t the assistant using my file? (Used in Training check)",
    // Added
    "Summarize the key points from my documents.",
    "Where does the policy mention reimbursements?",
    "Extract all important dates and deadlines from the files.",
    "What are the top 3 risks mentioned and their mitigations?",
    "Find the section that explains pricing or billing terms.",
    "Give me an executive summary in bullet points.",
    "Compare the onboarding docs and list differences.",
  ];
};

/* ====== Text Beautification Helpers ====== */

/** Strip OpenAI/annotation-looking citations like:  or  */
const stripCitations = (text = "") => {
  let cleaned = text.replace(/【[^】]*】/g, "");
  cleaned = cleaned.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  return cleaned;
};

/** Split into code + text blocks using ``` fences */
const splitCodeBlocks = (text) => {
  const parts = String(text).split(/```/g);
  const out = [];
  for (let i = 0; i < parts.length; i++) {
    const raw = parts[i];
    if (i % 2 === 1) {
      const cleaned = raw.replace(/^[a-zA-Z0-9_-]+\n/, "");
      out.push({ type: "code", text: cleaned });
    } else if (raw.trim()) {
      out.push({ type: "text", text: raw });
    }
  }
  return out.length ? out : [{ type: "text", text }];
};

const MarkdownBlock = ({ text = "" }) => {
  const lines = text.split(/\r?\n/);

  const groups = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    const h3 = line.match(/^###\s+(.*)$/);
    const h2 = line.match(/^##\s+(.*)$/);
    const h1 = line.match(/^#\s+(.*)$/);
    if (h3 || h2 || h1) {
      groups.push({ type: "heading", level: h1 ? 1 : h2 ? 2 : 3, text: (h1?.[1] || h2?.[1] || h3?.[1] || "").trim() });
      i++; continue;
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, "").trim());
        i++;
      }
      groups.push({ type: "ol", items });
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, "").trim());
        i++;
      }
      groups.push({ type: "ul", items });
      continue;
    }

    if (line.trim() !== "") {
      const buff = [line];
      i++;
      while (i < lines.length && lines[i].trim() !== "" && !/^(#{1,3}\s+|\s*[-*]\s+|\s*\d+\.\s+)/.test(lines[i])) {
        buff.push(lines[i]);
        i++;
      }
      groups.push({ type: "p", text: buff.join(" ") });
      continue;
    }

    groups.push({ type: "br" });
    i++;
  }

  const renderInline = (s) => {
    const parts = [];
    let lastIndex = 0;
    const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))/g;
    let m;
    while ((m = regex.exec(s)) !== null) {
      if (m.index > lastIndex) {
        parts.push(<span key={`t-${lastIndex}`}>{s.slice(lastIndex, m.index)}</span>);
      }
      const token = m[0];
      if (token.startsWith("**")) {
        parts.push(<strong key={`b-${m.index}`}>{token.slice(2, -2)}</strong>);
      } else if (token.startsWith("*")) {
        parts.push(<em key={`i-${m.index}`}>{token.slice(1, -1)}</em>);
      } else if (m[2] && m[3]) {
        parts.push(
          <a key={`a-${m.index}`} href={m[3]} target="_blank" rel="noreferrer">
            {m[2]}
          </a>
        );
      }
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < s.length) {
      parts.push(<span key={`t-end`}>{s.slice(lastIndex)}</span>);
    }
    return parts;
  };

  return (
    <Stack spacing={0.75}>
      {groups.map((g, idx) => {
        if (g.type === "heading") {
          const variant = g.level === 1 ? "h6" : g.level === 2 ? "subtitle1" : "subtitle2";
          return (
            <Typography key={idx} variant={variant} sx={{ fontWeight: 800 }}>
              {renderInline(g.text)}
            </Typography>
          );
        }
        if (g.type === "ol") {
          return (
            <Box key={idx} component="ol" sx={{ pl: 3, m: 0 }}>
              {g.items.map((it, j) => <li key={j}><Typography variant="body1" component="span">{renderInline(it)}</Typography></li>)}
            </Box>
          );
        }
        if (g.type === "ul") {
          return (
            <Box key={idx} component="ul" sx={{ pl: 3, m: 0 }}>
              {g.items.map((it, j) => <li key={j}><Typography variant="body1" component="span">{renderInline(it)}</Typography></li>)}
            </Box>
          );
        }
        if (g.type === "p") {
          return <Typography key={idx} variant="body1" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{renderInline(g.text)}</Typography>;
        }
        return <Box key={idx} sx={{ height: 6 }} />;
      })}
    </Stack>
  );
};

const MessageBubble = ({ role, content, timestamp, onCopy, copied, userInitial = "U", sources = [], onOpenSource }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isUser = role === "user";

  const bubbleBg = isUser
    ? alpha(theme.palette.primary.main, isDark ? 0.18 : 0.10)
    : alpha(theme.palette.secondary.main, isDark ? 0.18 : 0.10);

  const cleaned = stripCitations(content || "");

  // Normalize sources to { name, key }
  const srcs = Array.isArray(sources) ? sources.map((s, i) => {
    if (typeof s === "string") return { name: s, key: s };
    return { name: s.name || s.key || `Source ${i+1}`, key: s.key || s.name || `ref-${i}` };
  }) : [];

  return (
    <Box display="flex" justifyContent={isUser ? "flex-end" : "flex-start"}>
      <Box
        sx={{
          maxWidth: "85%",
          bgcolor: bubbleBg,
          color: "text.primary",
          borderRadius: 2,
          px: 2,
          py: 1.2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <RichText content={cleaned} />

        {/* Sources under assistant answers */}
        {!isUser && srcs.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
              Sources
            </Typography>
            <Stack spacing={0.75}>
              {srcs.map((s, idx) => {
                const isUrl = /^https?:\/\//i.test(String(s.key)) || String(s.key).includes("/urls/");
                return (
                  <Box
                    key={`${s.key}-${idx}`}
                    onClick={() => onOpenSource?.(s)}
                    sx={{
                      p: 0.75,
                      borderRadius: 1,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      border: "1px dashed",
                      borderColor: "divider",
                      bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.06 : 0.04),
                      "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.12 : 0.08) }
                    }}
                    title={s.key}
                  >
                    {isUrl ? <LinkIcon fontSize="small" /> : <DescriptionIcon fontSize="small" />}
                    <Typography variant="body2" noWrap sx={{ flex: 1, fontWeight: 600 }}>
                      {s.name}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.75} gap={1}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {timestamp ? new Date(timestamp).toLocaleTimeString() : ""}
          </Typography>
          <Tooltip title={copied ? "Copied!" : "Copy"}>
            <IconButton size="small" onClick={onCopy} sx={{ opacity: 0.85 }}>
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {isUser && (
        <Avatar sx={{ bgcolor: "primary.main", width: 28, height: 28, ml: 1 }}>{userInitial}</Avatar>
      )}
    </Box>
  );
};

const RichText = ({ content = "" }) => {
  const theme = useTheme();
  const blocks = splitCodeBlocks(content);

  const codeBg = theme.palette.mode === "dark" ? "#0b1020" : "#0f172a";
  const codeFg = theme.palette.mode === "dark" ? "#cbd5e1" : "#e2e8f0";

  return (
    <Stack spacing={1}>
      {blocks.map((b, i) =>
        b.type === "code" ? (
          <Box
            key={i}
            component="pre"
            sx={{
              m: 0,
              p: 1.2,
              borderRadius: 1.5,
              overflow: "auto",
              bgcolor: codeBg,
              color: codeFg,
              fontSize: 12,
            }}
          >
            <code>{b.text}</code>
          </Box>
        ) : (
          <MarkdownBlock key={i} text={b.text} />
        )
      )}
    </Stack>
  );
};

const EmptyState = () => (
  <Box
    sx={{
      height: "100%",
      minHeight: 240,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "text.secondary",
      textAlign: "center",
      px: 3,
    }}
  >
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
        Ask anything to get started
      </Typography>
      <Typography variant="body2">
        Press <b>Enter</b> to send, <b>Shift+Enter</b> for a new line.
      </Typography>
    </Box>
  </Box>
);

const TypingDots = () => {
  const theme = useTheme();
  return (
    <Box
      px={2}
      py={1}
      borderRadius={2}
      bgcolor="action.hover"
      color="text.primary"
      maxWidth="70%"
      display="flex"
      alignItems="center"
      style={{ ["--dot-color"]: theme.palette.text.secondary }}
    >
      <span className="dot" />
      <span className="dot" />
      <span className="dot" />
      <style>
        {`
        .dot {
          height: 7px;
          width: 7px;
          margin: 0 2px;
          background-color: var(--dot-color);
          border-radius: 50%;
          display: inline-block;
          animation: blink 1.4s infinite both;
        }
        .dot:nth-child(2) { animation-delay: 0.2s; }
        .dot:nth-child(3) { animation-delay: 0.4s; }
        @keyframes blink {
          0% { opacity: .2; }
          20% { opacity: 1; }
          100% { opacity: .2; }
        }
      `}
      </style>
    </Box>
  );
};

export default ChatInterface;
